import { OctTablePagination } from '@oceantech/oceantech-ui';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';
import { ConfirmDialog } from 'src/app/modules/component/ConfirmDialog';
import InputSearch from 'src/app/modules/component/InputSearch';
import { TableCustom } from 'src/app/modules/component/table/table-custom/TableCustom';
import { usePhanHeDieuTriNoiTruContext } from 'src/app/modules/phan-he-noi-tru-dieu-tri/PhanHeDieuTriContext';
import { CODE, KEY, RESPONSE_MESSAGE, SELECTION_MODE } from 'src/app/modules/utils/Constant';
import { localStorageItem } from 'src/app/modules/utils/LocalStorage';
import { rowsForPage } from 'src/app/modules/utils/PageUtils';
import { SERVICE_TEMPLATE_COLUMNS_ACTIONS, SERVICE_TEMPLATE_DEFAULT_COLUMNS } from './constants';
import { deleteTemplate, getTemplates } from './services';
import { TableTemplateProps, TemplateListModalProps } from './types';

const TYPE_SEARCH = {
	CURRENT: 'current',
	OLD: 'history',
};

const TemplateListModal = ({
	open,
	handleClose,
	onSelect,
	tableTitle = 'Danh sách mẫu',
	getRowData,
	columns,
	renderAddModal,
	multiSelect,
	searchFunc,
	searchParams,
	isViewHistory,
}: TemplateListModalProps) => {
	const { benhNhanDieuTri } = usePhanHeDieuTriNoiTruContext();
	const [tableData, setTableData] = useState<TableTemplateProps>({
		data: [],
		paging: {
			totalPages: 0,
			totalElements: 0,
			numberOfElements: 0,
		},
	});
	const [selectedRow, setSelectedRow] = useState<any>();
	const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
	const [keySearch, setKeySearch] = useState('');
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [query, setQuery] = useState({
		keySearch: '',
		pageIndex: 1,
		pageSize: 10,
	});
	const [typeSearch, setSearchType] = useState<string>(TYPE_SEARCH.OLD);

	const userInfo = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);
	const AddModalComponent = renderAddModal?.({
		open: openAddDialog,
		onSave: (data: any) => {
			console.log(data);
		},
		onClose: () => {
			setOpenAddDialog(false);
		},
	});

	useEffect(() => {
		handleGetTemplates(query);
	}, [query, typeSearch]);

	const handleGetTemplates = async (query: any) => {
		try {
			const { code, data } = await searchFunc({
				name: query.keySearch,
				pageIndex: query.pageIndex,
				pageSize: query.pageSize,
				...searchParams,
				...(isViewHistory && typeSearch === TYPE_SEARCH.OLD && { patientId: benhNhanDieuTri?.patientId }),
				...(isViewHistory && typeSearch === TYPE_SEARCH.CURRENT && { visitId: benhNhanDieuTri?.visitId }),
			});

			if (CODE.SUCCESS === code && data) {
				const result = data.content.map((item: any) =>
					getRowData
						? getRowData({
								...item,
								rowId: item.id,
						  })
						: {
								...item,
								tenNguoiTao: userInfo?.name,
								rowId: item.id,
						  }
				);
				setTableData({
					data: result,
					paging: {
						totalPages: data.totalPages,
						totalElements: data.totalElements,
						numberOfElements: data.numberOfElements,
					},
				});
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	const onXoaDonMau = async () => {
		try {
			const rowIdDelete = selectedRow.rowId || selectedRow[0]?.rowId;
			const { code } = await deleteTemplate(rowIdDelete);
			if (CODE.SUCCESS === code) {
				toast.success('Xóa đơn mẫu thành công');
				handleGetTemplates(query);
				setSelectedRow(null);
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setOpenConfirmDialog(false);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>): any => {
		setKeySearch(event.target.value.trim());
	};

	const onSave = () => {
		if (!selectedRow) {
			toast.warn('Chưa chọn mẫu');
			return;
		}

		onSelect?.(multiSelect ? selectedRow.map((row: any) => row.rowId) : selectedRow[0].rowId);
	};

	const onActionClick = (action: string, rowData: any) => {
		const actionMap: Record<string, () => void> = {
			delete: () => {
				setSelectedRow(rowData);
				setOpenConfirmDialog(true);
			},
		};
		actionMap[action]?.();
	};

	const handleChangeKeySearch = (): any => {
		setQuery({
			...query,
			keySearch: keySearch,
		});
	};

	return (
		<Modal size="xl" show={open} centered onHide={handleClose}>
			<Modal.Header className="py-3 header-modal" closeButton>
				<Modal.Title>{tableTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body className="pt-16 pb-10 px-24 spaces">
				<Row>
					<Col xs={12}>
						<Row>
							{isViewHistory && (
								<Col xs={4}>
									<Form.Group
										className="d-flex spaces pt-8"
										controlId="formBasicCheckbox"
									>
										<Form.Check
											inline
											type="radio"
											label="Toàn bộ lần điều trị"
											checked={TYPE_SEARCH.OLD === typeSearch}
											onChange={() => setSearchType(TYPE_SEARCH.OLD)}
											className="pe-3"
										/>
										<Form.Check
											inline
											type="radio"
											label="Lần điều trị hiện tại"
											checked={TYPE_SEARCH.CURRENT === typeSearch}
											onChange={() => setSearchType(TYPE_SEARCH.CURRENT)}
										/>
									</Form.Group>
								</Col>
							)}
							<Col xs>
								<InputSearch
									handleChange={handleChange}
									handleSearch={handleChangeKeySearch}
									handleKeyDown={(e: React.KeyboardEvent) => e.key === KEY.ENTER && handleChangeKeySearch()}
									placeholder="Tìm kiếm"
									type="text"
								/>
							</Col>
						</Row>
						<div className="mt-3 spaces">
							<TableCustom
								data={tableData.data}
								columns={columns ? columns : SERVICE_TEMPLATE_DEFAULT_COLUMNS}
								columnsActions={!isViewHistory && SERVICE_TEMPLATE_COLUMNS_ACTIONS}
								getSelectedRowsData={setSelectedRow}
								selectionMode={
									multiSelect
										? SELECTION_MODE.MULTI
										: SELECTION_MODE.SINGLE_NO_RADIO_BUTTON
								}
								minHeight={400}
								maxHeight={400}
								tableOptions={{
									onActionClick,
								}}
							/>
							<OctTablePagination
								rowsPerPage={query.pageSize}
								rowsForPage={rowsForPage}
								page={query.pageIndex}
								totalPages={tableData.paging.totalPages}
								totalElements={tableData.paging.totalElements}
								numberOfElements={tableData.paging.numberOfElements}
								setPage={(value: number) => {
									setQuery({
										...query,
										pageIndex: value,
									});
								}}
								setRowsPerPage={(value: number) => {
									setQuery({
										...query,
										pageSize: value,
									});
								}}
							/>
						</div>
					</Col>
				</Row>
			</Modal.Body>

			<Modal.Footer className="d-flex justify-content-end">
				<Button className="btn-outline" onClick={handleClose}>
					Đóng
				</Button>
				<Button className="btn-fill" onClick={onSave}>
					Sử dụng
				</Button>
			</Modal.Footer>

			{openConfirmDialog && (
				<ConfirmDialog
					className="z-index-1060"
					show={openConfirmDialog}
					title="Thông báo"
					message="Bạn có chắc chắn muốn xóa mẫu này không ?"
					yes="Có"
					close="Không"
					onCloseClick={() => setOpenConfirmDialog(false)}
					onYesClick={() => {
						onXoaDonMau();
					}}
				/>
			)}

			{AddModalComponent}
		</Modal>
	);
};

export default TemplateListModal;
